import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@fragments';
import { IconBan } from '@tabler/icons-react';
import Popover from '../Popover';
import FillPicker, { toPaint } from './FillPicker';
import SolidPicker from './SolidPicker';
import { fillToCss } from '../../../model/colors';
import './styles.scss';

export { FillPicker };

// A plain color (a stroke, an icon, a text highlight), inline in a popover of its own.
// `value` and `onChange` are hex strings (null when `allowNone` clears it).
export function ColorPicker({ value, onChange, brand = [], used = [], allowNone = false }) {
  const { t } = useTranslation('artEditor');
  return <div className="art-fill">
    {allowNone && <button type="button" className={`art-fill__none ${!value ? 'is-active' : ''}`} aria-pressed={!value} onClick={() => onChange(null)}>
      <IconBan aria-hidden="true" /><Text.caption contrast={7}>{t('colorNone')}</Text.caption>
    </button>}
    <SolidPicker color={value || '#ffffff'} brand={brand} used={used} onChange={({ color }) => onChange(color)} />
  </div>;
}

// A swatch that opens the fill picker. `modes` are the tabs the target takes ('solid',
// 'gradient', 'image'); `alpha` adds opacity to colors; `image` holds the photo actions
// ({ onUpload, onGallery, onMove }). With only 'solid' and no alpha, `value` and
// `onChange` are hex strings; otherwise paints ({ type: solid | linear | radial | image }).
// `allowGradient` is the older spelling of modes ['solid', 'gradient'].
export default function ColorField({ value, onChange, label, brand, used, allowNone, allowGradient, modes, alpha = false, image, placement = 'top', size = 'medium', dock = true }) {
  const tabs = modes || (allowGradient ? ['solid', 'gradient'] : ['solid']);
  const plain = tabs.length === 1 && tabs[0] === 'solid' && !alpha;
  const paint = toPaint(value);
  const emit = next => onChange(plain ? next?.color || null : next);
  return <Popover label={label} placement={placement} width={272} className="is-fill" dock={dock} trigger={
    <button type="button" className={`art-color-trigger is-${size}`} aria-label={label} title={label}>
      <span className={!paint ? 'is-none' : ''}>{paint && <i style={{ background: fillToCss(paint) }} />}</span>
    </button>}>
    {({ close }) => <FillPicker value={paint} onChange={emit} modes={tabs} alpha={alpha} allowNone={allowNone} brand={brand} used={used} image={image} close={close} />}
  </Popover>;
}
